import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button } from '@material-ui/core';
import { yupResolver } from '@hookform/resolvers';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  useSaveJurisdictionMutation,
  useLazyCheckJurisdictionDuplicateQuery,
} from '../../../redux/rtkQuery/apiWikiSlice';
import { objectKeysToSnakeCase } from '../../../utils';
import { useStyles } from './Styles';
import { RootState } from '../../../redux/root';
import { setJurisdictions } from '../../../redux/slices/wikiSlice';
import { InputAutocompletePlaces, InputBaseContainer, InputTextWithLabel } from '../../../molecules';
import getSchema from './schema';
import StatesDropdown from '../SharedComponents/StatesDropdown';
import JurisdictionFormCounty from '../../../forms/JurisdictionFormCounty';

const AddJurisdictionContainer = ({ onClose }) => {
  const [saveJurisdiction] = useSaveJurisdictionMutation();
  const jurisdictions = useSelector((s: RootState) => s.wikiSlice.jurisdictions);
  const dispatch = useDispatch();
  const methods = useForm({
    shouldUnregister: false,
    defaultValues: {},
    resolver: yupResolver(getSchema(jurisdictions)),
    mode: 'all',
  });
  const county: any = methods.watch('county');
 useEffect(() => {
    if (county) {
      methods.setValue('websitePropertyAppraiser', county.websitePropertyAppraiser);
    }
  }, [county]);

  const address: any = methods.watch('address');
  const [duplicate, setDuplicate] = useState<{ id: number; jurisdictions: string } | null>(null);
  const [triggerDuplicateCheck] = useLazyCheckJurisdictionDuplicateQuery();
  useEffect(() => {
    const trimmed = (address || '').trim();
    if (!trimmed) {
      setDuplicate(null);
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        const result = await triggerDuplicateCheck(trimmed).unwrap();
        if (result?.duplicate && result.jurisdiction) {
          setDuplicate({
            id: result.jurisdiction.id,
            jurisdictions: result.jurisdiction.jurisdictions,
          });
        } else {
          setDuplicate(null);
        }
      } catch {
        setDuplicate(null);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [address, triggerDuplicateCheck]);

  const classes = useStyles();
  const onSubmit = async data => {
    if (duplicate) {
      return;
    }
    if (!data.county) {
      methods.setError('county', { message: 'County is required!' });
      return;
    }
    const payload = objectKeysToSnakeCase({
      ...data,
      county: data.county.name,
      state: data.state?.id,
    });
    try {
      const result = await saveJurisdiction(payload).unwrap();
      dispatch(setJurisdictions([result, ...jurisdictions]));
      onClose();
    } catch (error) {
      if (error?.data) {
        Object.entries(error.data).forEach(([field, messages]) => {
          methods.setError(field, {
            type: 'server',
            message: Array.isArray(messages) ? messages[0] : messages,
          });
        });
      }
    }
  };

  const handlePlaceSelect = (address, isSelect) => {
    if (address || isSelect) {
      methods.clearErrors('address');
    }
    if (isSelect) {
      return Object.entries(address).map(([key, value]) => {
        methods.setValue(key, value);
        methods.clearErrors(key);
      });
    }
    return methods.setValue('address', address);
  };

  return (
    <div>
      <Content>
        <div style={{ minHeight: '50%' }}>
          <form onSubmit={methods.handleSubmit(onSubmit, err => console.log(err))}>
            <StatesDropdown {...methods} />
            <div>
              <JurisdictionFormCounty
                openTop={false}
                {...methods}
              />
              {methods.errors?.county && <div>{methods.errors?.county?.message}</div>}
            </div>

            <InputBaseContainer>
              <Controller
                as={InputTextWithLabel}
                control={methods.control}
                name="jurisdictions"
                helperText={methods.errors?.jurisdictions?.message}
                error={methods.errors?.jurisdictions?.message}
                label="Jurisdiction"
                isRequired
              />
              <Controller
                as={InputTextWithLabel}
                control={methods.control}
                name="jurisdictionWebsite"
                helperText={methods.errors?.jurisdictionWebsite?.message}
                error={methods.errors?.jurisdictionWebsite?.message}
                label="Jurisdiction Website"
                isRequired
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <Controller
                name="address"
                as={InputAutocompletePlaces}
                defaultValue=""
                control={methods.control}
                helperText={methods.errors?.address?.message}
                error={methods.errors?.address?.message}
                label="Address"
                handleFieldChange={handlePlaceSelect}
                isRequired
              />
            </InputBaseContainer>
            {duplicate && (
              <DuplicateCallout role="alert">
                A jurisdiction with this address already exists:{' '}
                <strong>{duplicate.jurisdictions}</strong>
              </DuplicateCallout>
            )}
            <InputBaseContainer>
              <Controller
                as={InputTextWithLabel}
                name="street"
                control={methods.control}
                helperText={methods.errors?.street?.message}
                error={methods.errors?.street?.message}
                label="Street"
                isRequired
              />
              <Controller
                as={InputTextWithLabel}
                name="city"
                control={methods.control}
                helperText={methods.errors?.city?.message}
                error={methods.errors?.city?.message}
                label="City"
                isRequired
              />
              <Controller
                as={InputTextWithLabel}
                name="zipCode"
                control={methods.control}
                helperText={methods.errors?.zipCode?.message}
                error={methods.errors?.zipCode?.message}
                label="Zip Code"
                isRequired
              />
            </InputBaseContainer>

            <Box display="flex" justifyContent="center" style={{ paddingTop: '5vh' }}>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                type="submit"
                disabled={!!duplicate}
              >
                Save
              </Button>
            </Box>
          </form>
        </div>
      </Content>
    </div>
  );
};

export default AddJurisdictionContainer;

const Content = styled.div`
  margin: 0 35px;
`;

const Error = styled.div`
  color: red;
  font-size: 12px;
  margin-left: 12px;
`;

const DuplicateCallout = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--danger-50);
  border: 1px solid var(--danger-500);
  color: var(--danger-700, #b42318);
  font-size: 0.8125rem;
  line-height: 1.4;
`;
